import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import LogoutIcon from '@mui/icons-material/Logout';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import RouterOutlinedIcon from '@mui/icons-material/RouterOutlined';
import { useAuth } from '../app/AuthProvider';
import { useToast } from '../app/ToastProvider';
import { NAV_ITEMS } from './navigation';
import { ChangePasswordDialog } from '../components/ChangePasswordDialog';

interface AppHeaderProps {
  sidebarWidth: number;
  onToggleSidebar: () => void;
  onOpenMobileSidebar: () => void;
}

function currentPageTitle(pathname: string): string {
  const match = NAV_ITEMS.find(
    (item) => pathname === item.to || pathname.startsWith(`${item.to}/`),
  );
  return match?.label ?? 'HiveCore CRM';
}

export function AppHeader({ sidebarWidth, onToggleSidebar, onOpenMobileSidebar }: AppHeaderProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase();

  const handleLogout = async (): Promise<void> => {
    setAnchorEl(null);
    await logout();
    toast.success('You have been signed out.');
    navigate('/login', { replace: true });
  };

  return (
    <AppBar
      position="fixed"
      color="inherit"
      elevation={0}
      sx={{
        width: { md: `calc(100% - ${sidebarWidth}px)` },
        ml: { md: `${sidebarWidth}px` },
        borderBottom: '1px solid',
        borderColor: 'divider',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ gap: 1 }}>
        <IconButton
          onClick={onOpenMobileSidebar}
          edge="start"
          sx={{ display: { md: 'none' } }}
          aria-label="Open navigation"
        >
          <MenuIcon />
        </IconButton>
        <IconButton
          onClick={onToggleSidebar}
          edge="start"
          sx={{ display: { xs: 'none', md: 'inline-flex' } }}
          aria-label="Collapse or expand navigation"
        >
          <MenuIcon />
        </IconButton>

        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <RouterOutlinedIcon color="primary" />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap sx={{ lineHeight: 1.2 }}>
              HiveCore
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {currentPageTitle(location.pathname)}
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title={user ? `${user.firstName} ${user.lastName}` : 'Account'}>
          <IconButton onClick={(event) => setAnchorEl(event.currentTarget)} aria-label="Account menu">
            <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: 14 }}>
              {initials || '?'}
            </Avatar>
          </IconButton>
        </Tooltip>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2">
              {user ? `${user.firstName} ${user.lastName}` : 'Signed in'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
          <Divider />
          <MenuItem onClick={() => { setAnchorEl(null); setPasswordOpen(true); }}>
            <ListItemIcon>
              <LockResetOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Change password
          </MenuItem>
          <MenuItem onClick={() => void handleLogout()}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Sign out
          </MenuItem>
        </Menu>
        <ChangePasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />
      </Toolbar>
    </AppBar>
  );
}
